import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authAPI } from "../services/api";
import { setSessionExpiresAt, setUser, SESSION_DURATION_MS } from "../utils/auth";
import "./Login.css";
import { showError, showSwal } from "../utils/swalHelper";
import { sanitizeClientErrorMessage } from "../utils/apiErrorMessage";
import banner1 from "../images/csrp-banner1.jpg";
import banner2 from "../images/csrp-banner2.jpg";
import banner3 from "../images/csrp-banner3.jpg";
import logo from "../images/greenlam-logo.png";

const IconUser = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="#7a7a7a"
    viewBox="0 0 16 16"
  >
    <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3Zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
  </svg>
);

const IconLock = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="#7a7a7a"
    viewBox="0 0 16 16"
  >
    <path d="M3.5 8V6a4.5 4.5 0 0 1 9 0v2h.5A1.5 1.5 0 0 1 14.5 9.5v5A1.5 1.5 0 0 1 13 16H3a1.5 1.5 0 0 1-1.5-1.5v-5A1.5 1.5 0 0 1 3 8h.5Zm1 0h7V6a3.5 3.5 0 0 0-7 0v2Z" />
  </svg>
);

const IconEye = ({ closed }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="#7a7a7a"
    viewBox="0 0 16 16"
  >
    {closed ? (
      <>
        <path d="M13.359 11.238C14.262 10.393 15 9.324 15 8c0-1.324-.738-2.393-1.641-3.238a7.666 7.666 0 0 0-1.145-.89l-.708.708c.32.197.628.413.918.646C13.717 5.947 14.5 6.898 14.5 8c0 1.102-.783 2.053-2.076 3.004-.29.233-.598.449-.918.646l.708.708c.4-.266.78-.557 1.145-.89Z" />
        <path d="M11.297 9.176a3.5 3.5 0 0 0-4.473-4.473l.77.772A2.5 2.5 0 0 1 10.524 8l.773.773Z" />
        <path d="M5.786 6.319a2.5 2.5 0 0 0 3.895 3.895l.73.73a3.5 3.5 0 0 1-5.355-4.625l.73.73Z" />
        <path d="m2.354 2.646 11 11-.708.708-11-11 .708-.708Z" />
        <path d="M2.641 4.762C1.738 5.607 1 6.676 1 8c0 1.324.738 2.393 1.641 3.238.364.333.744.624 1.145.89l.708-.708A6.866 6.866 0 0 1 2.5 10.996C1.283 10.053.5 9.102.5 8c0-1.102.783-2.053 2.076-3.004.29-.233.598-.449.918-.646l-.708-.708c-.4.266-.78.557-1.145.89Z" />
      </>
    ) : (
      <>
        <path d="M8 3c-3.314 0-6 3-6 5s2.686 5 6 5 6-3 6-5-2.686-5-6-5Zm0 1c2.761 0 5 2.582 5 4s-2.239 4-5 4-5-2.582-5-4 2.239-4 5-4Z" />
        <path d="M8 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z" />
      </>
    )}
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      image: banner1,
      fallback:
        "linear-gradient(135deg, #6c79f0 0%, #7952b3 60%, #6b4ba5 100%)",
    },
    {
      image: banner2,
      fallback:
        "linear-gradient(135deg, #5d87ff 0%, #8c5cf4 60%, #6b4ba5 100%)",
    },
    {
      image: banner3,
      fallback:
        "linear-gradient(135deg, #5b7bfa 0%, #6b5be4 60%, #5a4bb5 100%)",
    },
  ];

  useEffect(() => {
    const id = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4500);
    return () => clearInterval(id);
  }, [slides.length]);

  const completeLogin = (loginPayload) => {
    // Cookie-based session: only store user data for UI.
    setUser(loginPayload?.user ?? loginPayload?.User);
    // 120-minute sliding session: store expiry from server for warning + auto-logout.
    const expiresAtRaw =
      loginPayload?.expiresAt ?? loginPayload?.ExpiresAt ?? null;
    const expiresAtMs = expiresAtRaw ? Date.parse(expiresAtRaw) : NaN;
    setSessionExpiresAt(
      Number.isFinite(expiresAtMs) ? expiresAtMs : Date.now() + SESSION_DURATION_MS
    );

    const user = loginPayload?.user ?? loginPayload?.User;

    // Check user type/roles to determine redirect path
    const userTypeCode = (
      user?.UserTypeCode ||
      user?.userTypeCode ||
      user?.UserType ||
      user?.userType ||
      ""
    )
      .toString()
      .trim()
      .toLowerCase();

    const roles = Array.isArray(user?.Roles) ? user.Roles : [];
    const roleCodes = roles
      .map((role) => {
        if (!role) return "";
        if (typeof role === "string") return role;
        return (
          role.RoleCode ||
          role.roleCode ||
          role.RoleName ||
          role.roleName ||
          ""
        );
      })
      .map((value) => value.toString().trim().toLowerCase())
      .filter(Boolean);

    const userTypeName = (
      user?.UserTypeName ||
      user?.userTypeName ||
      user?.Designation ||
      user?.designation ||
      ""
    )
      .toString()
      .trim()
      .toLowerCase();

    const isSEByName = /\bse\b|\(se\)/i.test(userTypeName);
    // UT0000024 = "DOS Admin" (domestic coordinator) — not system admin (UT0000017 = "Admin Role").
    const isDosAdmin =
      userTypeCode === "ut0000024" || /^dos\s+admin$/i.test(userTypeName.trim());
    const isAdminByName =
      !isDosAdmin &&
      (/^admin\s+role$/i.test(userTypeName.trim()) ||
        /\badministrator\b/i.test(userTypeName));

    const isAdmin =
      userTypeCode === "admin" ||
      userTypeCode === "sysadmin001" ||
      userTypeCode === "ut0000017" ||
      roleCodes.includes("admin") ||
      isAdminByName;
    const isSE =
      userTypeCode === "se" ||
      userTypeCode === "ut0000001" ||
      roleCodes.includes("se") ||
      isSEByName;

    // Force a full page reload to ensure App.js re-renders with new auth state
    // This is necessary because React Router's navigate() might not trigger
    // a re-render of App.js before it checks isAuthenticated()
    // Landing dashboard mapping:
    // admin -> admin dashboard
    // se -> se dashboard
    // all non-admin and non-se users -> approver dashboard
    let redirectPath = "/approver-dashboard";
    if (isAdmin) {
      redirectPath = "/admin-dashboard";
    } else if (isSE) {
      redirectPath = "/dashboard";
    }
    window.location.href = redirectPath;
  };

  // Returning from Microsoft Entra ID sign-in (MFA): ?azureMfa=success | cancelled | unauthorized | expired | failed
  useEffect(() => {
    const status = new URLSearchParams(window.location.search).get("azureMfa");
    if (!status) return;
    window.history.replaceState(null, "", window.location.pathname);

    if (status !== "success") {
      const messages = {
        cancelled: "Microsoft sign-in was cancelled. Please log in again.",
        unauthorized:
          "The Microsoft account you signed in with does not match the entered User Name / Email.",
        notregistered:
          "Your Microsoft account is not registered as an active CSRP user. Please contact the administrator.",
        expired: "Your login session expired. Please log in again.",
      };
      const msg = messages[status] || "Microsoft verification failed. Please log in again.";
      showError(msg);
      setError(msg);
      return;
    }

    setLoading(true);
    authAPI
      .completeAzureLogin()
      .then((res) => {
        const loginPayload = res?.data?.data ?? res?.data?.Data ?? res?.data;
        if (!res?.success || !(loginPayload?.user ?? loginPayload?.User)) {
          throw new Error("Microsoft login could not be completed");
        }
        showSwal({
          title: "Login successful. Redirecting...",
          icon: "success",
          toast: true,
          position: "top",
          timer: 1200,
          showConfirmButton: false,
          customClass: {},
        });
        setTimeout(() => completeLogin(loginPayload), 900);
      })
      .catch(() => {
        const msg = "Microsoft verification could not be completed. Please log in again.";
        showError(msg);
        setError(msg);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    let redirecting = false;

    try {
      if (!username.trim()) {
        const msg = "User Name / Email is required";
        showError(msg);
        setError(msg);
        setLoading(false);
        return;
      }

      // Company Microsoft email: the API returns the Microsoft sign-in URL (local password not used).
      const response = await authAPI.login(username.trim(), password);

      if (response.success && response.data) {
        const loginPayload = response.data?.data ?? response.data?.Data ?? response.data;

        const azureMfaUrl =
          loginPayload?.azureMfaRedirectUrl ?? loginPayload?.AzureMfaRedirectUrl;
        if ((loginPayload?.requiresAzureMfa ?? loginPayload?.RequiresAzureMfa) && azureMfaUrl) {
          redirecting = true;
          window.location.assign(azureMfaUrl);
          return;
        }

        completeLogin(loginPayload);
      } else {
        const msg = response.message || "Login failed. Please try again.";
        showError(msg);
        setError(msg);
      }
    } catch (err) {
      console.error("Login error:", err);
      console.error("Error response:", err.response?.data);
      let errorMessage = "An error occurred during login. Please try again.";

      if (
        err.code === "ERR_NETWORK" ||
        err.message?.includes("Network Error")
      ) {
        errorMessage =
          "Cannot connect to server. Please ensure all backend services are running.";
      } else if (err.response?.status === 400) {
        // Bad Request - show sanitized backend message only
        const backendError =
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Invalid request. Please check your credentials.";
        errorMessage = sanitizeClientErrorMessage(
          backendError,
          "Invalid request. Please check your credentials."
        );
      } else if (err.response?.data?.message) {
        errorMessage = sanitizeClientErrorMessage(
          err.response.data.message,
          "An error occurred during login. Please try again."
        );
      } else if (err.message) {
        errorMessage = sanitizeClientErrorMessage(
          err.message,
          "An error occurred during login. Please try again."
        );
      }

      showError(errorMessage);
      setError(errorMessage);
    } finally {
      if (!redirecting) setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-bg">
        {slides.map((slide, idx) => (
          <div
            key={idx}
            className={`login-bg-layer ${idx === currentSlide ? "active" : ""}`}
            style={{
              backgroundImage: `url(${slide.image}), ${slide.fallback}`,
            }}
          />
        ))}
      </div>
      <div className="login-card">
        <div className="login-logo">
          <img src={logo} alt="Greenlam Industries Limited" />
        </div>

        <div className="login-body">
          <h2>Login</h2>

          {error && <div className="login-alert">{error}</div>}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="login-field">
              <label className="login-label">
                User Name / Email <span className="required">*</span>
              </label>
              <div className="input-wrap">
                <span className="input-icon">
                  <IconUser />
                </span>
                <input
                  type="text"
                  className="login-input"
                  placeholder="Enter User Name or Microsoft Email"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="login-field">
              <label className="login-label">
                Password <span className="required">*</span>
              </label>
              <div className="input-wrap">
                <span className="input-icon">
                  <IconLock />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  className="login-input"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="eye-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label="Toggle password visibility"
                >
                  <IconEye closed={!showPassword} />
                </button>
              </div>
            </div>

            <div className="login-links">
              <a
                href="https://selfservice.greenlam.com/login"
                target="_blank"
                rel="noopener noreferrer"
              >
                Forgot Password?
              </a>
            </div>

            <button type="submit" className="login-submit" disabled={loading}>
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
